/**
 * IndiaMarketDashboard.js - WITH HELP TOOLTIPS
 * =============================================
 * Clean design with helpful explanations for users
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  TrendingUp, TrendingDown, RefreshCw, Clock, ArrowUpRight, ArrowDownRight,
  Building2, AlertCircle, Activity, BarChart3, Target, HelpCircle, Info
} from 'lucide-react';
import { useMarket } from '../context/MarketContext';

const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

// Help tooltip component
const HelpTooltip = ({ text }) => {
  const [show, setShow] = useState(false);
  
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <HelpCircle 
        size={14} 
        color="#64748b"
        style={{ cursor: 'pointer' }}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      />
      {show && (
        <div style={{
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginBottom: '8px',
          padding: '10px 12px',
          background: '#1e293b',
          border: '1px solid #475569',
          borderRadius: '8px',
          fontSize: '12px',
          color: '#e2e8f0',
          width: '220px',
          zIndex: 1000,
          boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
          lineHeight: '1.4'
        }}>
          {text}
          <div style={{
            position: 'absolute',
            bottom: '-6px',
            left: '50%',
            transform: 'translateX(-50%) rotate(45deg)',
            width: '10px',
            height: '10px',
            background: '#1e293b',
            borderRight: '1px solid #475569',
            borderBottom: '1px solid #475569'
          }} />
        </div>
      )}
    </div>
  );
};

const IndiaMarketDashboard = ({ onStockClick }) => {
  const { isMarketOpen } = useMarket();
  
  const [indices, setIndices] = useState(null);
  const [optionChain, setOptionChain] = useState(null);
  const [fiiDii, setFiiDii] = useState(null);
  const [topGainers, setTopGainers] = useState([]);
  const [topLosers, setTopLosers] = useState([]);
  const [indicators, setIndicators] = useState(null);
  const [etfs, setEtfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [etfLoading, setEtfLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const POPULAR_ETFS = [
    { symbol: 'NIFTYBEES', name: 'Nifty 50 ETF', help: 'Tracks NIFTY 50 index. 1 unit ≈ 1/100th of NIFTY value' },
    { symbol: 'BANKBEES', name: 'Bank Nifty ETF', help: 'Tracks Bank NIFTY index. Exposure to top banking stocks' },
    { symbol: 'GOLDBEES', name: 'Gold ETF', help: 'Tracks domestic gold price. 1 unit ≈ 0.01g of gold' },
    { symbol: 'SILVERBEES', name: 'Silver ETF', help: 'Tracks silver price. 1 unit ≈ 1g of silver (higher NAV)' },
    { symbol: 'ITBEES', name: 'IT Sector ETF', help: 'Tracks NIFTY IT index. Top IT companies exposure' },
    { symbol: 'JUNIORBEES', name: 'Next 50 ETF', help: 'Tracks NIFTY Next 50. Mid-cap growth stocks' },
    { symbol: 'LIQUIDBEES', name: 'Liquid ETF', help: 'Invests in overnight securities. Ultra-safe, low returns' },
    { symbol: 'CPSEETF', name: 'CPSE ETF', help: 'Central Public Sector Enterprises. Government companies' },
    { symbol: 'PSUBNKBEES', name: 'PSU Bank ETF', help: 'Public sector banks. Higher risk, cyclical' },
    { symbol: 'N100', name: 'NASDAQ 100', help: 'US NASDAQ 100 index. International diversification' }
  ];

  const INDICATOR_HELP = {
    'RBI Repo Rate': 'Rate at which RBI lends to banks. Higher = tighter credit',
    'India CPI Inflation': 'Consumer price increase YoY. RBI targets 4% (±2%)',
    'India GDP Growth': 'Quarterly GDP growth rate. India averages 6-7%',
    '10-Year G-Sec Yield': 'Government bond yield. Benchmark for all rates',
    'INR/USD Rate': 'Rupees per dollar. Higher = weaker rupee',
    'Foreign Reserves': 'RBI forex reserves. Buffer against currency crisis',
    'Manufacturing PMI': 'Factory activity. >50 = expansion, <50 = contraction',
    'Services PMI': 'Services activity. >50 = expansion. 55%+ of GDP'
  };

  const fetchETFPrices = useCallback(async () => {
    setEtfLoading(true);
    try {
      const etfData = await Promise.all(
        POPULAR_ETFS.map(async (etf) => {
          try {
            const res = await fetch(`${API_URL}/india/quote/${etf.symbol}`);
            if (res.ok) {
              const data = await res.json();
              return { ...etf, price: data.price, change: data.change, changePercent: data.changePercent };
            }
          } catch (err) { }
          return { ...etf, price: null };
        })
      );
      setEtfs(etfData.filter(e => e.price !== null));
    } catch (err) {
      console.error('Error fetching ETF prices:', err);
    } finally {
      setEtfLoading(false);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [indicesRes, indicatorsRes, optionsRes, fiiRes, moversRes] = await Promise.all([
        fetch(`${API_URL}/india/indices`),
        fetch(`${API_URL}/india/indicators`),
        fetch(`${API_URL}/india/options/NIFTY`),
        fetch(`${API_URL}/india/fii-dii`),
        fetch(`${API_URL}/india/market-movers`)
      ]);

      if (indicesRes.ok) setIndices(await indicesRes.json());
      if (indicatorsRes.ok) setIndicators(await indicatorsRes.json());
      if (optionsRes.ok) setOptionChain(await optionsRes.json());
      if (fiiRes.ok) setFiiDii(await fiiRes.json());
      if (moversRes.ok) {
        const data = await moversRes.json();
        setTopGainers(data.gainers || []);
        setTopLosers(data.losers || []);
      }
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchETFPrices();
    const interval = setInterval(() => { fetchData(); fetchETFPrices(); }, 60000);
    return () => clearInterval(interval);
  }, [fetchData, fetchETFPrices]);

  const formatNum = (num, decimals = 2) => {
    if (num === null || num === undefined || isNaN(num)) return '-';
    return Number(num).toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const formatCompact = (num) => {
    if (!num) return '-';
    const n = Math.abs(num);
    if (n >= 10000000) return `${(num / 10000000).toFixed(1)}Cr`;
    if (n >= 100000) return `${(num / 100000).toFixed(1)}L`;
    if (n >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toFixed(0);
  };

  const getActionText = (name, indicator) => {
    if (!indicator) return null;
    const map = {
      'RBI Repo Rate': indicator.value >= 6.5 ? 'HOLD' : 'GROWTH',
      'India CPI Inflation': indicator.value > 5 ? 'HEDGE' : 'NEUTRAL',
      'India GDP Growth': indicator.value >= 5 ? 'CYCLICAL' : 'DEFENSIVE',
      '10-Year G-Sec Yield': indicator.value > 7 ? 'BONDS' : 'EQUITY',
      'INR/USD Rate': indicator.changePercent > 2 ? 'WEAK ₹' : 'STABLE',
      'Foreign Reserves': indicator.value > 600 ? 'STRONG' : 'MONITOR',
      'Manufacturing PMI': indicator.value > 50 ? 'EXPAND' : 'CONTRACT',
      'Services PMI': indicator.value > 50 ? 'EXPAND' : 'CONTRACT'
    };
    return map[name] || null;
  };

  if (loading && !indices) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '300px', color: '#94a3b8' }}>
        <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: '12px' }} />
        <span>Loading India Market Data...</span>
      </div>
    );
  }

  if (error && !indices) {
    return (
      <div style={{ background: '#1e293b', borderRadius: '12px', padding: '40px', textAlign: 'center' }}>
        <AlertCircle size={40} color="#ef4444" style={{ marginBottom: '12px' }} />
        <p style={{ color: '#f87171', margin: '0 0 12px' }}>Failed to load data</p>
        <button onClick={fetchData} style={{ padding: '8px 16px', background: '#ef4444', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer' }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
            <span style={{ fontSize: '28px' }}>🇮🇳</span>
            <h2 style={{ margin: 0, color: '#f1f5f9', fontSize: '20px', fontWeight: '700' }}>India Market</h2>
            <span style={{
              padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
              background: isMarketOpen() ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
              color: isMarketOpen() ? '#22c55e' : '#ef4444'
            }}>
              {isMarketOpen() ? 'OPEN' : 'CLOSED'}
            </span>
          </div>
          <p style={{ margin: 0, color: '#64748b', fontSize: '13px' }}>
            NSE/BSE • {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : ''}
          </p>
        </div>
        <button
          onClick={() => { fetchData(); fetchETFPrices(); }}
          disabled={loading}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '8px 16px', background: '#3b82f6', border: 'none',
            borderRadius: '8px', color: 'white', cursor: 'pointer',
            fontSize: '13px', fontWeight: '600'
          }}
        >
          <RefreshCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          Refresh
        </button>
      </div>

      {/* Market Indices */}
      <div style={{ background: '#1e293b', borderRadius: '12px', border: '1px solid #334155', marginBottom: '16px', overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <BarChart3 size={16} color="#60a5fa" />
          <span style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '600' }}>Market Indices</span>
          <HelpTooltip text="Major market indices from NSE and BSE. NIFTY and SENSEX are the primary benchmarks for Indian equities." />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1px', background: '#334155' }}>
          {indices?.data?.slice(0, 5).map((idx, i) => {
            const isPositive = idx.change >= 0;
            return (
              <div key={i} style={{ background: '#1e293b', padding: '14px' }}>
                <div style={{ color: '#64748b', fontSize: '11px', fontWeight: '500', marginBottom: '4px' }}>{idx.index || idx.name}</div>
                <div style={{ color: '#f1f5f9', fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>{formatNum(idx.last || idx.value)}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ color: isPositive ? '#22c55e' : '#ef4444', fontSize: '12px', fontWeight: '600' }}>
                    {isPositive ? '+' : ''}{(idx.percentChange || idx.pctChange)?.toFixed(2)}%
                  </span>
                  <span style={{ color: '#64748b', fontSize: '11px' }}>{isPositive ? '+' : ''}{formatNum(idx.change)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Economic Indicators */}
      {indicators && (
        <div style={{ background: '#1e293b', borderRadius: '12px', border: '1px solid #334155', marginBottom: '16px', overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Activity size={16} color="#f59e0b" />
            <span style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '600' }}>Economic Indicators</span>
            <HelpTooltip text="Key economic metrics that affect market direction. Action labels suggest investment positioning based on current values." />
            <span style={{ marginLeft: 'auto', color: '#64748b', fontSize: '11px' }}>RBI / MOSPI</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1px', background: '#334155' }}>
            {Object.entries(indicators).map(([name, ind]) => {
              const isPositive = ind.status === 'up';
              const upIsBad = ['India CPI Inflation', 'INR/USD Rate'].includes(name);
              const color = upIsBad ? (isPositive ? '#ef4444' : '#22c55e') : (isPositive ? '#22c55e' : '#ef4444');
              const action = getActionText(name, ind);
              
              return (
                <div key={name} style={{ background: '#1e293b', padding: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ color: '#94a3b8', fontSize: '11px', fontWeight: '500' }}>{name}</span>
                      <HelpTooltip text={INDICATOR_HELP[name] || ind.description} />
                    </div>
                    {isPositive ? <TrendingUp size={14} color={color} /> : <TrendingDown size={14} color={color} />}
                  </div>
                  <div style={{ color: '#f1f5f9', fontSize: '20px', fontWeight: '700', marginBottom: '2px' }}>
                    {ind.unit === '%' ? `${ind.value.toFixed(2)}%` : 
                     ind.unit === '$B' ? `$${ind.value.toFixed(0)}B` :
                     ind.unit === '₹' ? `₹${ind.value.toFixed(2)}` : ind.value.toFixed(2)}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: color, fontSize: '12px', fontWeight: '600' }}>
                      {ind.changePercent > 0 ? '+' : ''}{ind.changePercent?.toFixed(2)}%
                    </span>
                    {action && (
                      <span style={{
                        fontSize: '9px', fontWeight: '700', color: color,
                        padding: '2px 6px', background: `${color}15`, borderRadius: '4px'
                      }}>
                        {action}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ETFs with Note */}
      {etfs.length > 0 && (
        <div style={{ background: '#1e293b', borderRadius: '12px', border: '1px solid #334155', marginBottom: '16px', overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <TrendingUp size={16} color="#22c55e" />
            <span style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '600' }}>Popular ETFs</span>
            <HelpTooltip text="ETF prices depend on their NAV at launch and units tracked. Gold ETF may be cheaper than Silver ETF due to different unit sizes (1 Gold unit = 0.01g, 1 Silver unit = 1g)." />
            <button
              onClick={fetchETFPrices}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#3b82f6', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
            >
              <RefreshCw size={12} style={{ animation: etfLoading ? 'spin 1s linear infinite' : 'none' }} />
            </button>
          </div>
          
          {/* ETF explanation banner */}
          <div style={{ padding: '10px 16px', background: 'rgba(59, 130, 246, 0.1)', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Info size={14} color="#60a5fa" />
            <span style={{ color: '#94a3b8', fontSize: '11px' }}>
              ETF unit prices vary based on underlying asset quantity per unit. Hover/tap on ? icon for details.
            </span>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '1px', background: '#334155' }}>
            {etfs.map((etf, i) => {
              const isPositive = etf.changePercent >= 0;
              return (
                <div 
                  key={i} 
                  onClick={() => onStockClick?.(etf.symbol)}
                  style={{ background: '#1e293b', padding: '12px', cursor: 'pointer', transition: 'background 0.2s' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#273548'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#1e293b'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                    <span style={{ color: '#94a3b8', fontSize: '10px', fontWeight: '500' }}>{etf.name}</span>
                    <HelpTooltip text={etf.help} />
                  </div>
                  <div style={{ color: '#f1f5f9', fontSize: '16px', fontWeight: '700', marginBottom: '4px' }}>₹{etf.price?.toFixed(2)}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {isPositive ? <TrendingUp size={10} color="#22c55e" /> : <TrendingDown size={10} color="#ef4444" />}
                    <span style={{ color: isPositive ? '#22c55e' : '#ef4444', fontSize: '11px', fontWeight: '600' }}>
                      {isPositive ? '+' : ''}{etf.changePercent?.toFixed(2)}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* PCR & FII/DII */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '16px' }}>
        {/* PCR */}
        <div style={{ background: '#1e293b', borderRadius: '12px', border: '1px solid #334155', overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Target size={16} color="#a78bfa" />
            <span style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '600' }}>Put-Call Ratio</span>
            <HelpTooltip text="PCR = Put OI / Call OI. High PCR (>1.2) is bullish (more put writers), Low PCR (<0.8) is bearish (more call buyers)." />
          </div>
          <div style={{ padding: '16px' }}>
            {(() => {
              const pcr = optionChain?.pcr?.pcr || 0;
              const sentiment = pcr > 1.2 ? 'Bullish' : pcr < 0.8 ? 'Bearish' : 'Neutral';
              const color = pcr > 1.2 ? '#22c55e' : pcr < 0.8 ? '#ef4444' : '#f59e0b';
              const pos = Math.min(Math.max((pcr - 0.5) / 1.0 * 100, 0), 100);
              
              return (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '16px' }}>
                    <span style={{ fontSize: '32px', fontWeight: '700', color }}>{pcr.toFixed(2)}</span>
                    <span style={{ padding: '4px 12px', borderRadius: '12px', background: `${color}15`, color, fontSize: '12px', fontWeight: '600' }}>{sentiment}</span>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#64748b', marginBottom: '4px' }}>
                      <span>Bearish</span><span>Neutral</span><span>Bullish</span>
                    </div>
                    <div style={{ height: '8px', background: 'linear-gradient(90deg, #ef4444, #f59e0b, #22c55e)', borderRadius: '4px', position: 'relative' }}>
                      <div style={{
                        position: 'absolute', top: '-4px', left: `${pos}%`,
                        width: '16px', height: '16px', background: 'white', borderRadius: '50%',
                        transform: 'translateX(-50%)', boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                      }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#94a3b8' }}>
                    <span>Call OI: {formatCompact(optionChain?.pcr?.totalCallOI)}</span>
                    <span>Put OI: {formatCompact(optionChain?.pcr?.totalPutOI)}</span>
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        {/* FII/DII */}
        <div style={{ background: '#1e293b', borderRadius: '12px', border: '1px solid #334155', overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Building2 size={16} color="#f59e0b" />
            <span style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '600' }}>Institutional Activity</span>
            <HelpTooltip text="FII = Foreign Institutional Investors (overseas funds). DII = Domestic Institutional Investors (MFs, insurance). Positive = net buying." />
          </div>
          <div style={{ padding: '16px' }}>
            {(() => {
              const fii = fiiDii?.data?.find(d => d.category === 'FII');
              const dii = fiiDii?.data?.find(d => d.category === 'DII');
              
              if (!fii && !dii) {
                return (
                  <div style={{ textAlign: 'center', color: '#64748b', padding: '20px 0' }}>
                    <Clock size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
                    <p style={{ margin: 0, fontSize: '13px' }}>Updates after 6 PM IST</p>
                  </div>
                );
              }
              
              const fiiVal = fii?.netValue || 0;
              const diiVal = dii?.netValue || 0;
              
              return (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <span style={{ color: '#94a3b8', fontSize: '12px' }}>FII (Foreign)</span>
                      <span style={{ color: fiiVal >= 0 ? '#22c55e' : '#ef4444', fontSize: '14px', fontWeight: '700' }}>
                        {fiiVal >= 0 ? '+' : ''}₹{formatCompact(fiiVal)}
                      </span>
                    </div>
                    <div style={{ height: '6px', background: '#0f172a', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{
                        width: `${Math.min(Math.abs(fiiVal) / 5000 * 100, 100)}%`, height: '100%',
                        background: fiiVal >= 0 ? '#22c55e' : '#ef4444', borderRadius: '3px'
                      }} />
                    </div>
                  </div>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <span style={{ color: '#94a3b8', fontSize: '12px' }}>DII (Domestic)</span>
                      <span style={{ color: diiVal >= 0 ? '#3b82f6' : '#f59e0b', fontSize: '14px', fontWeight: '700' }}>
                        {diiVal >= 0 ? '+' : ''}₹{formatCompact(diiVal)}
                      </span>
                    </div>
                    <div style={{ height: '6px', background: '#0f172a', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{
                        width: `${Math.min(Math.abs(diiVal) / 5000 * 100, 100)}%`, height: '100%',
                        background: diiVal >= 0 ? '#3b82f6' : '#f59e0b', borderRadius: '3px'
                      }} />
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Top Movers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
        {/* Gainers */}
        <div style={{ background: '#1e293b', borderRadius: '12px', border: '1px solid #334155', overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <TrendingUp size={16} color="#22c55e" />
            <span style={{ color: '#22c55e', fontSize: '14px', fontWeight: '600' }}>Top Gainers</span>
            <HelpTooltip text="NIFTY 50 stocks with highest % gains today. Click to view details." />
          </div>
          <div>
            {topGainers.length > 0 ? topGainers.slice(0, 5).map((stock, i) => (
              <div 
                key={i}
                onClick={() => onStockClick?.(stock.symbol)}
                style={{ 
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 16px', borderBottom: '1px solid #334155', cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#273548'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <div>
                  <div style={{ color: '#f1f5f9', fontSize: '13px', fontWeight: '600' }}>{stock.symbol}</div>
                  <div style={{ color: '#64748b', fontSize: '11px' }}>₹{formatNum(stock.ltp)}</div>
                </div>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600',
                  background: 'rgba(34, 197, 94, 0.1)', color: '#22c55e'
                }}>
                  <ArrowUpRight size={12} />+{stock.pctChange?.toFixed(2)}%
                </div>
              </div>
            )) : (
              <div style={{ padding: '24px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
                Available during market hours
              </div>
            )}
          </div>
        </div>

        {/* Losers */}
        <div style={{ background: '#1e293b', borderRadius: '12px', border: '1px solid #334155', overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <TrendingDown size={16} color="#ef4444" />
            <span style={{ color: '#ef4444', fontSize: '14px', fontWeight: '600' }}>Top Losers</span>
            <HelpTooltip text="NIFTY 50 stocks with highest % losses today. Click to view details." />
          </div>
          <div>
            {topLosers.length > 0 ? topLosers.slice(0, 5).map((stock, i) => (
              <div 
                key={i}
                onClick={() => onStockClick?.(stock.symbol)}
                style={{ 
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 16px', borderBottom: '1px solid #334155', cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#273548'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <div>
                  <div style={{ color: '#f1f5f9', fontSize: '13px', fontWeight: '600' }}>{stock.symbol}</div>
                  <div style={{ color: '#64748b', fontSize: '11px' }}>₹{formatNum(stock.ltp)}</div>
                </div>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600',
                  background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444'
                }}>
                  <ArrowDownRight size={12} />{stock.pctChange?.toFixed(2)}%
                </div>
              </div>
            )) : (
              <div style={{ padding: '24px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
                Available during market hours
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default IndiaMarketDashboard;